/**
 * PATH: apps/billing/src/pages/SSOPage.tsx
 * PURPOSE: Legacy SSO callback handler for Billing Hub
 *
 * WHY: Handles /sso-callback route for cross-hub SSO token exchange.
 *      The primary SSO flow uses UnifiedSSOPage at /sso, but this page
 *      provides backward-compatible support for the legacy callback URL.
 */

import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '@quotech/auth'

const DEFAULT_REDIRECT = '/book_keeping'

export default function SSOPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { setFromSSO, checkAuth } = useAuthStore()

  const [status, setStatus] = useState<'processing' | 'success' | 'error' | 'timeout'>('processing')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [elapsed, setElapsed] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(e => e + 1)
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const token = searchParams.get('token')
    const returnTo = searchParams.get('to') || searchParams.get('returnTo') || DEFAULT_REDIRECT
    const orgId = searchParams.get('org_id') || searchParams.get('orgId')
    const orgName = searchParams.get('org_name') || searchParams.get('orgName')

    const timeoutId = setTimeout(() => {
      setStatus('timeout')
    }, 12000)

    const processSSO = async () => {
      try {
        // Check if already authenticated via shared cookie
        const alreadyAuthed = await checkAuth()
        if (alreadyAuthed) {
          clearTimeout(timeoutId)
          setStatus('success')
          setTimeout(() => navigate(returnTo, { replace: true }), 100)
          return
        }

        if (!token) {
          clearTimeout(timeoutId)
          window.location.href = 'https://dashboard.quotech.ai'
          return
        }

        const success = await setFromSSO(token, orgId, orgName)
        clearTimeout(timeoutId)

        if (success) {
          setStatus('success')
          setTimeout(() => navigate(returnTo, { replace: true }), 100)
        } else {
          setStatus('error')
          setErrorMessage('Authentication failed. Your session may have expired.')
        }
      } catch (err) {
        clearTimeout(timeoutId)
        setStatus('error')
        setErrorMessage(err instanceof Error ? err.message : 'An unexpected error occurred')
      }
    }

    processSSO()
    return () => clearTimeout(timeoutId)
  }, [searchParams, navigate, setFromSSO, checkAuth])

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="text-center">
          <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-slate-600 dark:text-slate-400">Signed in! Redirecting...</p>
        </div>
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="text-center max-w-md p-6">
          <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">Sign-in Failed</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">{errorMessage}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
            >
              Try Again
            </button>
            <a
              href="https://dashboard.quotech.ai"
              className="px-4 py-2 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-lg"
            >
              Back to Dashboard
            </a>
          </div>
        </div>
      </div>
    )
  }

  if (status === 'timeout') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="text-center max-w-md p-6">
          <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">Sign-in Timeout</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
            This might be a network issue. Try again or return to the dashboard.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
            >
              Try Again
            </button>
            <a
              href="https://dashboard.quotech.ai"
              className="px-4 py-2 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-lg"
            >
              Back to Dashboard
            </a>
          </div>
        </div>
      </div>
    )
  }

  // Processing state
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
      <div className="text-center">
        <div className="w-10 h-10 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">Signing you in to Billing...</p>
        {elapsed > 3 && (
          <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">This may take a moment...</p>
        )}
      </div>
    </div>
  )
}
